import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Container, Grid, Paper, Tabs, Tab, 
  Button, Divider, CircularProgress, Chip, IconButton,
  Card, CardContent, CardMedia, Tooltip, Badge, AppBar, Toolbar, useTheme, useMediaQuery, Collapse
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Camera as CameraIcon,
  Chat as ChatIcon,
  VideoCall as VideoCallIcon,
  Store as StoreIcon,
  Forum as ForumIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  Phone, Menu as MenuIcon, Close,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  SupportAgent as SupportAgentIcon,
  Gavel as GavelIcon,
  AttachMoney as AttachMoneyIcon,
  Home as HomeIcon,
  FileUpload as FileUploadIcon,
  Explore as ExploreIcon,
  FindInPage as FindInPageIcon,
  ShoppingCart as ShoppingCartIcon,
  Speed as SpeedIcon,
  Headset as HeadsetIcon,
  LocalShipping as LocalShippingIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import logo from '../assets/logo0.png';  // Adjust the path based on your file structure
import menuIcon from '../assets/menu.png';
import greenImage from '../assets/Green.png'; // Import the image

const Options = () => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Add new title section configuration
  const pageTitle = {
    main: "Smart Fix Panel",
    subtitle: "AI-Powered Solutions for Device Repair & Diagnostics",
    description: "Choose from our range of intelligent tools and expert services to diagnose, repair, and maintain your devices."
  };

  const sections = [
    {
      title: "AI Diagnostic Tools",
      description: "Upload device images for instant AI-powered fault detection. Use this tool to check if your device has any problems.",
      buttonText: "Start Diagnosis",
      icon: <CameraIcon sx={{ fontSize: 40 }} />,
      buttonIcon: <PlayArrowIcon />,
      bgColor: '#FFE01B',
      textColor: '#000000',
      action: () => navigate('/diagnostics')
    },
    {
      title: "Expert Support Network",
      description: "Connect with certified technicians for real-time assistance and professional guidance.",
      buttonText: "Find Expert",
      icon: <VideoCallIcon sx={{ fontSize: 40 }} />,
      buttonIcon: <SupportAgentIcon />,
      bgColor: '#18834D',
      textColor: '#FFFFFF',
      action: () => navigate('/expertconsultation')
    },
    {
      title: "Repair Marketplace",
      description: "Browse genuine parts and trusted repair services for your devices.",
      buttonText: "Explore Market",
      icon: <StoreIcon sx={{ fontSize: 40 }} />,
      buttonIcon: <ShoppingCartIcon />,
      bgColor: '#FFE01B',
      textColor: '#000000',
      action: () => navigate('/marketplace')
    }
  ];

  const menuItems = [
    { title: "Domains", href: "/domains", icon: <StorageIcon fontSize="small" /> },
    { title: "Webhosting", href: "/webhosting", icon: <StorageIcon fontSize="small" /> },
    { title: "Server", href: "/server", icon: <StorageIcon fontSize="small" /> },
    { title: "Website & Shop", href: "/website", icon: <StorageIcon fontSize="small" /> },
    { title: "Websicherheit", href: "/security", icon: <SecurityIcon fontSize="small" /> },
    { title: "Email & Office", href: "/email", icon: <StorageIcon fontSize="small" /> }
  ];

  const renderSection = (section, index) => (
    <Box
      key={index}
      sx={{
        position: 'relative',
        mb: 4,
        bgcolor: section.bgColor,
        color: section.textColor,
        minHeight: '300px',
        display: 'flex',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={3} sx={{ py: 6 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 4 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontFamily: '"Helvetica Neue", Arial, sans-serif'
                }}
              >
                {section.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontFamily: '"Helvetica Neue", Arial, sans-serif'
                }}
              >
                {section.description}
              </Typography>
              <Button
                variant="contained"
                onClick={section.action}
                startIcon={section.buttonIcon}
                sx={{
                  bgcolor: section.textColor,
                  color: section.bgColor,
                  borderRadius: 0,
                  py: 1.5,
                  px: 4,
                  '&:hover': {
                    bgcolor: section.textColor,
                    opacity: 0.9
                  }
                }}
              >
                {section.buttonText}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            {/* Image or illustration placeholder */}
            <Box
              sx={{
                height: '100%',
                minHeight: 300,
                bgcolor: 'rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {section.icon}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  const features = [
    {
      title: "Visual Analysis",
      price: "0,08 €* / pro Monat",
      description: "AI-powered image recognition for instant device diagnostics",
      icon: <CameraIcon />,
      actionIcon: <SearchIcon fontSize="small" />,
      link: "/ai-detect"
    },
    {
      title: "Live Support",
      price: "0,12 €* / pro Monat",
      description: "24/7 access to expert technicians via video call",
      icon: <VideoCallIcon />,
      actionIcon: <HeadsetIcon fontSize="small" />,
      link: "/expertconsultation"
    },
    {
      title: "Parts Marketplace",
      price: "Free",
      description: "Verified vendors and genuine replacement parts",
      icon: <StoreIcon />,
      actionIcon: <LocalShippingIcon fontSize="small" />,
      link: "/marketplace"
    }
  ];

  const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const handleLogoClick = () => navigate('/');
    const handleSupportClick = () => navigate('/support');

    return (
      <AppBar position="static" sx={{ bgcolor: '#000000', boxShadow: 'none' }}>
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 0.5
        }}>
          {/* Logo - Reduced size */}
          <Box
            component="img"
            src={logo}
            alt="Larvaram AI Logo"
            sx={{
              height: isMobile ? 24 : 30,
              cursor: 'pointer',
              filter: 'brightness(0) invert(1)',
            }}
            onClick={handleLogoClick}
          />

          {/* Desktop Menu */}
          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography 
                component="a" 
                href="tel:089998288026"
                sx={{ 
                  color: '#FFFFFF',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.9rem'
                }}
              >
                <Phone sx={{ fontSize: '1.2rem' }} /> 089 998 288 026
              </Typography>
              <Button
                variant="contained"
                onClick={handleSupportClick}
                startIcon={<SupportAgentIcon />}
                sx={{
                  bgcolor: '#18834D',
                  color: 'white',
                  '&:hover': { bgcolor: '#146c3c' }
                }}
              >
                Support
              </Button>
              <Button 
                sx={{ color: 'white' }} 
                onClick={() => navigate('/guidelines')}
                startIcon={<GavelIcon />}
              >
                Guidelines
              </Button>
              <Button 
                sx={{ color: 'white' }} 
                onClick={() => navigate('/pricing')}
                startIcon={<AttachMoneyIcon />}
              >
                Pricing
              </Button>
            </Box>
          ) : (
            // Mobile Menu Icon - Adjusted size
            <Box
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ 
                cursor: 'pointer',
                padding: '6px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box
                component="img"
                src={menuIcon}
                alt="Menu"
                sx={{
                  width: '20px',
                  height: '20px',
                  filter: 'brightness(0) invert(1)',
                  transition: 'transform 0.3s ease',
                  transform: mobileMenuOpen ? 'rotate(90deg)' : 'none'
                }}
              />
            </Box>
          )}
        </Toolbar>

        {/* Mobile Menu Dropdown */}
        <Collapse in={mobileMenuOpen && isMobile}>
          <Box sx={{ 
            bgcolor: '#000000',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography 
              component="a" 
              href="tel:089998288026"
              sx={{ 
                color: '#FFFFFF',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'center'
              }}
            >
              <Phone /> 089 998 288 026
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSupportClick}
              startIcon={<SupportAgentIcon />}
              sx={{
                bgcolor: '#18834D',
                color: 'white',
                '&:hover': { bgcolor: '#146c3c' }
              }}
            >
              Support
            </Button>
            <Button
              fullWidth
              onClick={() => navigate('/guidelines')}
              startIcon={<GavelIcon />}
              sx={{
                color: 'white',
                '&:hover': { color: '#18834D' }
              }}
            >
              Guidelines
            </Button>
            <Button
              fullWidth
              onClick={() => navigate('/pricing')}
              startIcon={<AttachMoneyIcon />}
              sx={{
                color: 'white',
                '&:hover': { color: '#18834D' }
              }}
            >
              Pricing
            </Button>
          </Box>
        </Collapse>
      </AppBar>
    );
  };

  const handleStartFileScan = () => {
    navigate('/file-scan');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000' }}>
      <Header />
      
      {/* Special Offer Banner */}
      <Box sx={{ 
        bgcolor: '#00a651', 
        color: '#fff', 
        py: 1, 
        textAlign: 'center',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #00ff84, transparent)'
        }
      }}>
        <Typography>
          Special Offer: First Month Free Trial
        </Typography>
      </Box>

      {/* Hero Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: { xs: 4, md: 8 },
          minHeight: '80vh'
        }}>
          {/* Content Section - Left */}
          <Box sx={{ 
            flex: 1,
            textAlign: { xs: 'center', md: 'left' },
            pr: { md: 4 }
          }}>
            <Typography 
              variant="h2" 
              sx={{ 
                color: '#00a651',
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textTransform: 'uppercase',
                letterSpacing: '1px',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -10,
                  left: 0,
                  width: '60px',
                  height: '4px',
                  backgroundColor: '#00a651',
                  display: { xs: 'none', md: 'block' }
                }
              }}
            >
              Smart Fix Panel
            </Typography>
            
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: '#fff',
                fontWeight: 500,
                lineHeight: 1.3
              }}
            >
              AI-Powered Solutions for Device Repair & Diagnostics
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4,
                opacity: 0.9,
                color: '#fff',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
                maxWidth: { xs: '100%', md: '80%' }
              }}
            >
              Choose from our range of intelligent tools and expert services to diagnose, repair, and maintain your devices.
            </Typography>

            <Button
              variant="contained"
              onClick={handleStartFileScan}
              startIcon={<FileUploadIcon />}
              sx={{
                bgcolor: '#00a651',
                color: '#fff',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                borderRadius: '4px',
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(0, 166, 81, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#008c44',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 166, 81, 0.4)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              START FILE SCAN
            </Button>
          </Box>

          {/* Image Section - Right */}
          <Box sx={{ 
            flex: 1,
            position: 'relative',
            height: { xs: '300px', md: '600px' },
            width: '100%',
            overflow: 'hidden',
            borderRadius: '8px',
            boxShadow: '0 0 30px rgba(0, 166, 81, 0.2)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(0,166,81,0.2), transparent)',
              zIndex: 1
            }
          }}>
            <Box
              component="img"
              src={greenImage}
              alt="Smart Fix Panel"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            />
          </Box>
        </Box>
      </Container>
      
      {sections.map(renderSection)}

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 6,
            fontWeight: 800,
            color: '#18834D'
          }}
        >
          Our Services
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                sx={{ 
                  borderRadius: 0,
                  border: '1px solid #e0e0e0',
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => navigate(feature.link)}
              >
                <CardContent>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 2
                  }}>
                    {feature.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography color="textSecondary" paragraph>
                    {feature.description}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#18834D',
                        fontWeight: 'bold'
                      }}
                    >
                      {feature.price}
                    </Typography>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      startIcon={feature.actionIcon}
                      sx={{
                        color: '#18834D',
                        '&:hover': {
                          backgroundColor: 'rgba(24, 131, 77, 0.1)'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Options; 